import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { PrimaryFilterKey } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { UseDimensionConfigPayload } from 'libs/end-to-end/src/use-dimension-config-payload';
import { EntitledDetailsInContextOfFiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { EmployeeGroupMenu } from 'apps/sigma2-ui/src/filters-v2/components/EmployeeGroupMenu';
import { ComparisonGroupMenu } from 'apps/sigma2-ui/src/filters-v2/components/ComparisonGroupMenu';
import React from 'react';
import { MetricMenu } from 'apps/sigma2-ui/src/filters-v2/components/MetricMenu';
import { MetricSingleMenu } from 'apps/sigma2-ui/src/filters-v2/components/MetricSingleMenu';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { DimensionMenu } from 'apps/sigma2-ui/src/filters-v2/components/DimensionMenu';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { PipelineAnalysisMenu } from 'apps/sigma2-ui/src/filters-v2/components/PipelineAnalysisMenu';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { GroupByMenu } from 'apps/sigma2-ui/src/filters-v2/components/GroupByMenu';
import { ViewByMenu } from 'apps/sigma2-ui/src/filters-v2/components/ViewByMenu';
import { BigTimePeriodComparisonMenu } from 'apps/sigma2-ui/src/filters-v2/components/BigTimePeriodComparisonMenu';
import { MetricMultipleMenu } from 'apps/sigma2-ui/src/filters-v2/components/MetricMultipleMenu';
import { SingleDateMenu } from 'apps/sigma2-ui/src/filters-v2/components/SingleDateMenu';
import { EventStudyGroupMenu } from 'apps/sigma2-ui/src/filters-v2/components/EventStudyGroupMenu';

// eslint-disable-next-line complexity
export const PrimaryFilterMenu = ({
  primaryFilterKey,
  allDimensions,
  entitledFilterFeatureDetails,
  getAllLevelsForDimension,
  feature,
  store,
  organizationId,
  databaseId,
  databaseRevisionId,
  focusArea,
  closeMenu,
  setPrimaryFilters,
  resetFiltersToInitialState,
  resetSecondaryFiltersToDefault,
  primaryFilterSelections,
  myObservation,
  secondaryFilterDefinitions,
  analysisName,
}: {
  primaryFilterKey: PrimaryFilterKey;
  allDimensions: DimensionDefinition[];
  entitledFilterFeatureDetails: EntitledDetailsInContextOfFiltersFeatureName;
  getAllLevelsForDimension: UseDimensionConfigPayload['getAllLevelsForDimension'];
  feature: FiltersFeatureName;
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  focusArea: FocusArea | null;
  closeMenu: () => void;
  setPrimaryFilters: (params: {
    selections: PrimaryFilterSelection[];
    shouldPreventAutoSubmit?: boolean;
  }) => void;
  resetFiltersToInitialState: (
    selectionsToPreserve?: PrimaryFilterSelection[],
  ) => void;
  resetSecondaryFiltersToDefault: (newFocusArea: FocusArea) => void;
  primaryFilterSelections: PrimaryFilterSelection[];
  myObservation: MyObservation | null;
  secondaryFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
  analysisName: string | undefined;
}) => {
  if (primaryFilterKey === 'employeeGroup') {
    return (
      <EmployeeGroupMenu
        allDimensions={allDimensions}
        getAllLevelsForDimension={getAllLevelsForDimension}
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        focusArea={focusArea}
        analysisName={analysisName}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
      />
    );
  }

  if (primaryFilterKey === 'comparisonGroup') {
    return (
      <ComparisonGroupMenu
        getAllLevelsForDimension={getAllLevelsForDimension}
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        focusArea={focusArea}
        analysisName={analysisName}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
      />
    );
  }

  if (primaryFilterKey === 'focusArea') {
    return (
      <MetricMenu
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        closeMenu={closeMenu}
        resetFiltersToInitialState={resetFiltersToInitialState}
        resetSecondaryFiltersToDefault={resetSecondaryFiltersToDefault}
        setPrimaryFilters={setPrimaryFilters}
        primaryFilterSelections={primaryFilterSelections}
        myObservation={myObservation}
        focusArea={focusArea}
        entitledFilterFeatureDetails={entitledFilterFeatureDetails}
      />
    );
  }

  if (primaryFilterKey === 'metric' && focusArea) {
    return (
      <MetricSingleMenu
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
        entitledFilterFeatureDetails={entitledFilterFeatureDetails}
      />
    );
  }

  if (primaryFilterKey === 'metrics') {
    return (
      <MetricMultipleMenu
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        setPrimaryFilters={setPrimaryFilters}
        closeMenu={closeMenu}
        entitledFilterFeatureDetails={entitledFilterFeatureDetails}
        focusArea={focusArea}
        analysisName={analysisName}
      />
    );
  }

  if (primaryFilterKey === 'dimension') {
    return (
      <DimensionMenu
        allDimensions={allDimensions}
        store={store}
        feature={feature}
        databaseRevisionId={databaseRevisionId}
        databaseId={databaseId}
        organizationId={organizationId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
      />
    );
  }

  if (primaryFilterKey === 'pipelineAnalysisConfiguration') {
    return (
      <PipelineAnalysisMenu
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
      />
    );
  }

  if (primaryFilterKey === 'groupBy') {
    return (
      <GroupByMenu
        allDimensions={allDimensions}
        store={store}
        feature={feature}
        databaseRevisionId={databaseRevisionId}
        databaseId={databaseId}
        organizationId={organizationId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
        secondaryFilterDefinitions={secondaryFilterDefinitions}
      />
    );
  }

  if (primaryFilterKey === 'secondaryBreakdown') {
    return (
      <GroupByMenu
        allDimensions={allDimensions}
        store={store}
        feature={feature}
        databaseRevisionId={databaseRevisionId}
        databaseId={databaseId}
        organizationId={organizationId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
        secondaryFilterDefinitions={secondaryFilterDefinitions}
        primaryFilterKey="secondaryBreakdown"
      />
    );
  }

  if (primaryFilterKey === 'viewBy') {
    return (
      <ViewByMenu
        store={store}
        feature={feature}
        databaseRevisionId={databaseRevisionId}
        databaseId={databaseId}
        organizationId={organizationId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
      />
    );
  }

  if (primaryFilterKey === 'bigTimePeriodComparison') {
    return (
      <BigTimePeriodComparisonMenu
        store={store}
        feature={feature}
        databaseRevisionId={databaseRevisionId}
        databaseId={databaseId}
        organizationId={organizationId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        focusArea={focusArea}
        analysisName={analysisName}
      />
    );
  }

  if (primaryFilterKey === 'singleDate') {
    return (
      <SingleDateMenu
        store={store}
        feature={feature}
        databaseRevisionId={databaseRevisionId}
        databaseId={databaseId}
        organizationId={organizationId}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
      />
    );
  }

  if (primaryFilterKey === 'eventStudyGroup') {
    return (
      <EventStudyGroupMenu
        allDimensions={allDimensions}
        getAllLevelsForDimension={getAllLevelsForDimension}
        secondaryFilterDefinitions={secondaryFilterDefinitions}
        store={store}
        feature={feature}
        organizationId={organizationId}
        databaseId={databaseId}
        databaseRevisionId={databaseRevisionId}
        focusArea={focusArea}
        closeMenu={closeMenu}
        setPrimaryFilters={setPrimaryFilters}
        myObservation={myObservation}
        analysisName={analysisName}
      />
    );
  }

  return null;
};
